import { loadScript } from '../shell/loadExternal.js';

const SDK_URL = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
let sdkPromise;

function paymentsSdk() {
    if (window.Klarna?.Payments) {
        return Promise.resolve(window.Klarna.Payments);
    }
    if (!sdkPromise) {
        sdkPromise = new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Klarna SDK timed out')), 15000);
            const ready = () => {
                if (window.Klarna?.Payments) {
                    clearTimeout(timeout);
                    resolve(window.Klarna.Payments);
                }
            };
            window.klarnaAsyncCallback = ready;
            loadScript(SDK_URL).then(ready).catch(() => {
                clearTimeout(timeout);
                reject(new Error('Klarna SDK unavailable'));
            });
        }).catch((error) => {
            sdkPromise = null;
            document.querySelector(`script[src="${SDK_URL}"]`)?.remove();
            throw error;
        });
    }
    return sdkPromise;
}

// The module reads raw JSON from php://input, unlike the form-encoded postAction helper.
async function sendKlarna(form, action, data) {
    const url = new URL(form.action);
    url.searchParams.set('cl', 'order');
    url.searchParams.set('fnc', 'updateKlarnaAjax');
    const shopId = new URLSearchParams(window.location.search).get('shp');
    if (shopId && !url.searchParams.has('shp')) url.searchParams.set('shp', shopId);

    const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        // KlarnaUtils::is_ajax() needs this header, otherwise a session reset answers with a 302 instead of JSON
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ action, ...data }),
        signal: AbortSignal.timeout(15000),
    });
    if (!response.ok) throw new Error('Klarna request failed');
    return response.json();
}

function callPayments(sdk, method, options) {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Klarna response timed out')), method === 'load' ? 15000 : 180000);
        const callback = (response) => {
            clearTimeout(timeout);
            resolve(response);
        };
        try {
            if (method === 'load') sdk.load(options, callback);
            else sdk[method](options, {}, callback);
        } catch (error) {
            clearTimeout(timeout);
            reject(error);
        }
    });
}

document.addEventListener('alpine:init', () => {
    Alpine.data('klarnaPayment', ({ step, clientToken, orderPaymentId = '', orderCategory = '', isB2B = false, errorText, companyText }) => {
        // resolved once: the error alert is teleported out of the form, where $el.closest('form') finds nothing
        let form;

        return {
            error: '',
            ready: null,

            init() {
                form = this.$el.closest('form');
                this.addBeforeSubmit(() => this.confirm());
                // Catch initialization errors here; a later submission retries SDK loading.
                this.initialize().catch(() => { this.error = errorText; });
            },

            initialize() {
                if (!this.ready) {
                    this.ready = (async () => {
                        if (!clientToken) throw new Error('Missing Klarna client token');
                        const sdk = await paymentsSdk();
                        sdk.init({ client_token: clientToken });
                        if (step === 'payment') {
                            await Promise.all([...this.form.querySelectorAll('.kp-radio')].map(async (radio) => {
                                const container = radio.closest('.kp-outer').querySelector('.kp-method');
                                if (container.classList.contains('alert')) return;
                                const answer = await callPayments(sdk, 'load', {
                                    container: `#${container.id}`,
                                    payment_method_category: radio.dataset.payment_id,
                                });
                                if (answer.show_form === false) {
                                    await this.unavailable(radio);
                                }
                            }));
                        }
                        return sdk;
                    })().catch((error) => {
                        this.ready = null;
                        throw error;
                    });
                }
                return this.ready;
            },

            get form() {
                return form;
            },

            get selectedRadio() {
                return [...this.form.querySelectorAll('.kp-radio')].find((radio) => radio.value === this.paymentId);
            },

            async unavailable(radio) {
                const outer = radio.closest('.kp-outer');
                radio.disabled = true;
                radio.dataset.klarnaUnavailable = '1';
                radio.checked = false;
                if (isB2B && radio.dataset.payment_id !== 'pay_later') {
                    const container = outer.querySelector('.kp-method');
                    container.classList.add('alert', 'alert-info');
                    container.textContent = companyText;
                } else {
                    outer.hidden = true;
                }
                if (this.paymentId === radio.value) {
                    this.paymentId = '';
                    this.shipSet = '';
                    await this.select();
                }
            },

            setHidden(name, value) {
                let input = this.form.querySelector(`input[name="${name}"]`);
                if (!input) {
                    input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = name;
                    this.form.append(input);
                }
                input.value = value;
            },

            async confirm() {
                this.error = '';
                if (step === 'payment' && !this.selectedRadio) return true;
                const paymentId = step === 'payment' ? this.paymentId : orderPaymentId;
                const category = step === 'payment' ? this.selectedRadio.dataset.payment_id : orderCategory;
                const revision = step === 'payment' ? this.selectionRevision : 0;
                try {
                    if (step === 'payment') {
                        await this.selectionPromise;
                        if (!this.selectionValid || this.selectedRadio?.dataset.klarnaUnavailable === '1'
                            || this.selectedRadio?.closest('.kp-outer').querySelector('.kp-method.alert')) {
                            throw new Error('Klarna selection unavailable');
                        }
                    }
                    const sdk = await this.initialize();
                    const unchanged = () => step === 'order' || (this.paymentId === paymentId
                        && this.selectionRevision === revision && !this.isSelecting);
                    if (!unchanged()) return false;
                    const data = { paymentId, client_token: clientToken };
                    if (step === 'payment') {
                        const update = await sendKlarna(this.form, 'addUserData', data);
                        if (update.status === 'redirect' || update.status === 'refresh') return this.navigate(update);
                        if (update.status !== 'updateUser') throw new Error('Klarna user update failed');
                        if (!unchanged()) return false;
                    }
                    const answer = await sendKlarna(this.form, 'checkOrderStatus', data);
                    if (!unchanged()) return false;
                    if (answer.status === 'redirect' || answer.status === 'refresh') return this.navigate(answer);
                    if (answer.status === 'submit' || (answer.status === 'finalize' && step === 'payment')) return true;

                    const method = { authorize: 'authorize', reauthorize: 'reauthorize', finalize: 'finalize' }[answer.status];
                    if (!method) throw new Error('Unexpected Klarna status');
                    const options = { payment_method_category: answer.data?.paymentMethod || category };
                    if (method === 'authorize') options.auto_finalize = false;
                    let result = await callPayments(sdk, method, options);
                    if (!unchanged()) return false;
                    if (result.approved !== true) {
                        if (step === 'payment' && result.show_form === false) await this.unavailable(this.selectedRadio);
                        throw new Error('Klarna authorization declined');
                    }
                    if (result.finalize_required === true && step === 'order') {
                        result = await callPayments(sdk, 'finalize', { payment_method_category: options.payment_method_category });
                        if (result.approved !== true || result.finalize_required === true) throw new Error('Klarna finalization failed');
                    }
                    const deferred = step === 'payment' && result.finalize_required === true;
                    if (!result.authorization_token && !deferred) throw new Error('Missing Klarna authorization token');
                    this.form.querySelector('input[name="sAuthToken"]')?.remove();
                    if (result.authorization_token) this.setHidden('sAuthToken', result.authorization_token);
                    this.form.querySelector('input[name="finalizeRequired"]')?.remove();
                    if (result.finalize_required === true) this.setHidden('finalizeRequired', 'true');
                    return true;
                } catch (error) {
                    this.form.querySelector('input[name="sAuthToken"]')?.remove();
                    this.form.querySelector('input[name="finalizeRequired"]')?.remove();
                    this.error = errorText;
                    // the alert sits at the top of the page, far from the submit button
                    if (step === 'payment') {
                        this.$nextTick(() => document.getElementById('klarna-payment-error')
                            ?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
                    }
                    return false;
                }
            },

            navigate(answer) {
                const url = answer.status === 'refresh' ? answer.data?.refreshUrl : answer.data?.url;
                if (!url) throw new Error('Missing Klarna redirect URL');
                return this.redirect(url);
            },
        };
    });
});
